import { useCallback, useMemo } from 'react'
import moment from 'moment/moment'
import { useSelector } from 'react-redux'
import { EhrAppointmentStateKind, UiInlineAlert } from '@src/types.ts'
import Storage, { useStorageValue } from '@utils/storage.ts'
import { Feature, useFeatureFlag } from '@components/FeatureFlag'
import { useActiveVideoSubscription } from '@src/hooks/useActiveVideoSubscription.ts'
import { usePaidCallCreditCount } from '@src/hooks/usePaidCallCreditCount.ts'
import { FULL_DISPLAY_TIME_FORMAT_WITH_COMMA } from '@config/momentFormat.ts'
import { useVideoCallProductType } from '@src/hooks/useVideoCallProductType.ts'
import { IconName } from '@components/base'
import { openUrl } from '@utils/openUrl'
import { uhcDeepLinkSelector } from '@selectors/app.ts'
import { EhrAppointmentCustomType } from '../utils'

export const useMissedCallAlert = (appointment: EhrAppointmentCustomType | null) => {
  const videoCallCreditsEnabled = useFeatureFlag(Feature.VideoCallCredits)
  const paidCallCreditCount = usePaidCallCreditCount()
  const { videoCallSubscriptionProduct } = useActiveVideoSubscription()
  const uhcDeepLinks = useSelector(uhcDeepLinkSelector)
  const missedCallAlertClosedKey = `${Storage.MISSED_CALL_ALERT_CLOSED_KEY}_${appointment?.id}`
  const [missedCallAlertClosed, setMissedCallAlertClosed] = useStorageValue(
    missedCallAlertClosedKey,
  )
  const { isHealthCoachingVideoCallProductType } = useVideoCallProductType()

  const onMissedCallAlertClose = useCallback(() => {
    if (appointment) {
      setMissedCallAlertClosed(true)
    }
  }, [appointment, setMissedCallAlertClosed])

  const showMissedCallAlert =
    videoCallCreditsEnabled &&
    videoCallSubscriptionProduct &&
    isHealthCoachingVideoCallProductType &&
    !missedCallAlertClosed &&
    appointment?.lastState.kind === EhrAppointmentStateKind.NoShow

  const missedCallAlertProps = useMemo((): {
    message: string
    alertCategory: UiInlineAlert
    rightIconName: IconName
    onRightIconPress: () => void
    onLeftIconPress?: () => void
    leftIconName?: IconName
    leftIconAccessibilityLabel?: string
  } | null => {
    if (!showMissedCallAlert) {
      return null
    }

    const formattedMeetingAt = moment(appointment.meetingAt).format(
      FULL_DISPLAY_TIME_FORMAT_WITH_COMMA,
    )

    if (paidCallCreditCount > 0) {
      return {
        message: `You missed your call on ${formattedMeetingAt} and used a call credit. Next time, make changes at least 24 hours in advance.`,
        alertCategory: UiInlineAlert.Info,
        rightIconName: 'x',
        onRightIconPress: onMissedCallAlertClose,
      }
    }

    if (videoCallSubscriptionProduct.uhc) {
      return {
        message: `Missed your call on ${formattedMeetingAt}? Go to the UHC Store to get more coaching calls.`,
        alertCategory: UiInlineAlert.Warning,
        rightIconName: 'caret-right',
        onRightIconPress: () => {
          openUrl(uhcDeepLinks.storeUrl)
        },
        leftIconName: 'x',
        onLeftIconPress: onMissedCallAlertClose,
        leftIconAccessibilityLabel: 'close',
      }
    }

    return null
  }, [
    appointment,
    onMissedCallAlertClose,
    paidCallCreditCount,
    showMissedCallAlert,
    uhcDeepLinks.storeUrl,
    videoCallSubscriptionProduct,
  ])

  return { showMissedCallAlert, missedCallAlertProps }
}
